import { Feature, FeatureCollection, Point } from "geojson";
import { RainfallBand } from "./types";
import { RainfallStation } from "./types";

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function toNumber(value: unknown): number | null {
  if (isFiniteNumber(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value.replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function isPointFeature(
  feature: Feature
): feature is Feature<Point, Record<string, unknown>> {
  return feature?.geometry?.type === "Point";
}

function isPolygonFeature(feature: Feature): boolean {
  const type = feature?.geometry?.type;
  return type === "Polygon" || type === "MultiPolygon";
}

function midValueForBand(band: RainfallBand): number {
  if (band.minMm === Number.NEGATIVE_INFINITY) {
    return Number.isFinite(band.maxMm) ? band.maxMm : 0;
  }
  if (band.maxMm === Number.POSITIVE_INFINITY) {
    return band.minMm + 1;
  }
  return (band.minMm + band.maxMm) / 2;
}

function estimateMmFromFill(
  fill: unknown,
  bands: RainfallBand[] | undefined
): number | null {
  if (!bands?.length || typeof fill !== "string" || !fill.trim()) {
    return null;
  }

  const normalized = fill.trim().toLowerCase();
  const band = bands.find(
    (item) => item.color.trim().toLowerCase() === normalized
  );
  return band ? midValueForBand(band) : null;
}

function pushStation(
  stations: RainfallStation[],
  candidate: RainfallStation
): void {
  if (
    !isFiniteNumber(candidate.lat) ||
    !isFiniteNumber(candidate.lng) ||
    !isFiniteNumber(candidate.precipitacaoMm)
  ) {
    return;
  }

  if (
    candidate.lat < -90 ||
    candidate.lat > 90 ||
    candidate.lng < -180 ||
    candidate.lng > 180
  ) {
    return;
  }

  stations.push(candidate);
}

export function extractRainfallStations(
  collection: FeatureCollection | null | undefined
): RainfallStation[] {
  if (!collection?.features?.length) {
    return [];
  }

  const stations: RainfallStation[] = [];

  collection.features.forEach((feature, index) => {
    if (!isPointFeature(feature)) {
      return;
    }

    const [lng, lat] = feature.geometry.coordinates ?? [];
    const precipitacaoMm = toNumber(feature.properties?.precipitacao);

    if (precipitacaoMm === null) {
      return;
    }

    pushStation(stations, {
      id: String(
        feature.properties?.id ??
          feature.id ??
          feature.properties?.codigo ??
          `station-${index}`
      ),
      lat,
      lng,
      precipitacaoMm,
    });
  });

  return stations;
}

export function extractRainfallStationsFromTalhoes(
  collection: FeatureCollection | null | undefined,
  bands?: RainfallBand[] | null
): RainfallStation[] {
  if (!collection?.features?.length) {
    return [];
  }

  const stations: RainfallStation[] = [];

  collection.features.forEach((feature, index) => {
    if (!isPolygonFeature(feature)) {
      return;
    }

    const lat = toNumber(feature.properties?.pontoCentralLat);
    const lng = toNumber(feature.properties?.pontoCentralLong);
    if (lat === null || lng === null) {
      return;
    }

    let precipitacaoMm = toNumber(feature.properties?.precipitacao);
    if (precipitacaoMm === null) {
      precipitacaoMm = estimateMmFromFill(
        feature.properties?.fill,
        bands ?? undefined
      );
    }

    if (precipitacaoMm === null) {
      return;
    }

    pushStation(stations, {
      id: String(
        feature.properties?.id ??
          feature.properties?.codigo ??
          `talhao-${index}`
      ),
      lat,
      lng,
      precipitacaoMm,
    });
  });

  return stations;
}

export function extractRainfallSamples(
  collection: FeatureCollection | null | undefined,
  bands?: RainfallBand[] | null
): RainfallStation[] {
  const fromPoints = extractRainfallStations(collection);
  if (fromPoints.length) {
    return fromPoints;
  }
  return extractRainfallStationsFromTalhoes(collection, bands);
}
